import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { DatePickerModule } from 'primeng/datepicker';
import { ContactGroupService } from 'src/app/private/services/contact-group.service';
import { ManageCampaignService } from 'src/app/private/services/manage-campaign.service';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import * as moment from 'moment';

// Custom validator for campaign name - allows only alphanumeric, hyphen, and underscore
export function campaignNameValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }
  
  const validPattern = /^[a-zA-Z0-9_-]+$/;
  const isValid = validPattern.test(control.value);
  
  return isValid ? null : { invalidCampaignName: true };
}

// Custom validator to prevent past date scheduling
export function futureDateValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }
  
  const selectedDate = new Date(control.value);
  const currentDate = new Date();
  
  // Set current time to start of today for date comparison
  currentDate.setHours(0, 0, 0, 0);
  selectedDate.setHours(0, 0, 0, 0);
  
  if (selectedDate < currentDate) {
    return { pastDate: true };
  }
  
  return null;
}

@Component({
  selector: 'app-add-campaign',
  imports: [SharedModule, DatePickerModule, CalendarModule],
  providers: [ConfirmationService],
  templateUrl: './add-campaign.component.html',
  styleUrl: './add-campaign.component.scss'
})
export class AddCampaignComponent {

  @Input() type: string = 'Create';
  @Output() closeDialog = new EventEmitter();
  @Output() reload = new EventEmitter();
  btnLoading = false;
  isSubmitted = false;

  @Input() emailTemplates: any[] = [];
  @Input() whatsappTemplates: any[] = [];
  @Input() contactGroups: any[] = [];
  @Input() campaignData: any;
  @Input() active: string = 'whatsapp';

  maxBatchSize: any;
  disabledBatchSizeInput: boolean = true;
  loadingBatchSize: boolean = false;


  selectedNumberOfBatch: any;

  selectedBatchDelay: any;
  selectedCallDelay: any;

  statusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Draft', label: 'Draft' },
    { value: 'Schedule', label: 'Schedule' },
    { value: 'Completed', label: 'Completed' }
  ];

  campaignForm: FormGroup = new FormGroup({});

  isSchedule: boolean = false;
  call_schedule_date_time: Date | null = null;
  minDate: Date = new Date(); // Minimum date for scheduling (current date)

  constructor(private fb: FormBuilder, private http: HttpClient,
    private api: ManageCampaignService,
    private localStorage: LocalStorageService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private contactGroupService: ContactGroupService
  ) {
    this.campaignForm = this.fb.group({
      event_name: ['', [Validators.required, campaignNameValidator]],
      broadcast_type: ['', Validators.required],
      template_id: ['', Validators.required],
      contact_group_id: ['', Validators.required],
      status: ['Pending'],
      is_schedule: [false],  // Fixed name for consistency
      schedule_date: [null],
      schedule_time: [null],
      no_of_batches: [null],
      subject: ['']  // Add subject field
    });

    // if this.active === 'call' then do not want validator for template_id
    if (this.active === 'call') {
      this.campaignForm.get('template_id')?.clearValidators();
      this.campaignForm.get('template_id')?.updateValueAndValidity();

      // Remove status validator for call campaigns
      this.campaignForm.get('status')?.clearValidators();
      this.campaignForm.get('status')?.updateValueAndValidity();
    }

    // Remove status validator for email and whatsapp campaigns
    if (this.active === 'email' || this.active === 'whatsapp') {
      this.campaignForm.get('status')?.clearValidators();
      this.campaignForm.get('status')?.updateValueAndValidity();
    }

    // Add subject validator for email campaigns
    if (this.active === 'email') {
      this.campaignForm.get('subject')?.setValidators([Validators.required]);
      this.campaignForm.get('subject')?.updateValueAndValidity();
    }
  }

  ngOnInit() {
    if (this.type === 'Update') {
      console.log('Campaign Data:', this.campaignData); // Debug log
      
      this.campaignForm.patchValue({
        event_name: this.campaignData.campaign_name,
        broadcast_type: this.campaignData.campaign_type,
        template_id: this.campaignData.template_id,
        contact_group_id: this.campaignData.contact_group_id,
        status: this.campaignData.status,
        is_schedule: this.campaignData.is_schedule,
        schedule_date: this.campaignData.schedule_date,
        schedule_time: this.campaignData.schedule_time,
        subject: this.campaignData.additional_details?.subject || ''
      });
      this.active = this.campaignData.campaign_type;
      console.log("activeeee", this.active);

      if (this.campaignData?.is_schedule && this.active === 'call') {
        this.isSchedule = true;
        
        // Handle different date formats that might come from the backend
        let scheduleDateTime: Date;
        
        if (this.campaignData.schedule_date && this.campaignData.schedule_time) {
          // Try to parse the date and time
          const dateStr = this.campaignData.schedule_date;
          const timeStr = this.campaignData.schedule_time;
          
          // Create a combined date-time string
          let combinedDateTime = `${dateStr} ${timeStr}`;
          
          // Try different parsing approaches
          scheduleDateTime = new Date(combinedDateTime);
          
          // If that fails, try ISO format
          if (isNaN(scheduleDateTime.getTime())) {
            scheduleDateTime = new Date(`${dateStr}T${timeStr}`);
          }
          
          // If still fails, try manual parsing
          if (isNaN(scheduleDateTime.getTime())) {
            // Try parsing date parts manually
            const dateParts = dateStr.split('-');
            const timeParts = timeStr.split(':');
            
            if (dateParts.length >= 3 && timeParts.length >= 2) {
              scheduleDateTime = new Date(
                parseInt(dateParts[0]), // year
                parseInt(dateParts[1]) - 1, // month (0-indexed)
                parseInt(dateParts[2]), // day
                parseInt(timeParts[0]), // hour
                parseInt(timeParts[1]) // minute
              );
            }
          }
          
          // Only set if we have a valid date
          if (!isNaN(scheduleDateTime.getTime())) {
            this.call_schedule_date_time = scheduleDateTime;
          } else {
            console.error('Unable to parse schedule date/time:', dateStr, timeStr);
            this.call_schedule_date_time = new Date(); // fallback to current date
          }
        } else {
          this.call_schedule_date_time = new Date();
        }
      }


      setTimeout(() => {
        if (this.campaignData.is_schedule) {
          // Handle schedule_date
          if (this.campaignData.schedule_date) {
            const scheduleDate = new Date(this.campaignData.schedule_date);
            if (!isNaN(scheduleDate.getTime())) {
              this.campaignForm.get('schedule_date')?.setValue(scheduleDate);
            }
          }
          
          // Handle schedule_time
          if (this.campaignData.schedule_time) {
            // For time, we need to create a proper date object
            const today = new Date();
            const timeParts = this.campaignData.schedule_time.split(':');
            
            if (timeParts.length >= 2) {
              const scheduleTime = new Date(today);
              scheduleTime.setHours(parseInt(timeParts[0]), parseInt(timeParts[1]), 0, 0);
              
              if (!isNaN(scheduleTime.getTime())) {
                this.campaignForm.get('schedule_time')?.setValue(scheduleTime);
              }
            }
          }
        }
      }, 500);

    }

    if (this.active === 'call') {
      console.log("here");
      this.contactGroups = this.contactGroups.filter((group) => group.contact_group_type === 'Call');

      if (this.type === 'Update' && this.campaignData.additional_details) {
        this.selectedNumberOfBatch = this.campaignData.additional_details.number_of_batches;
        this.selectedBatchDelay = this.campaignData.additional_details.batch_delay;
        this.selectedCallDelay = this.campaignData.additional_details.call_delay;
      }

      this.onContactGroupChange(this.campaignForm.get('contact_group_id')?.value);

    }else{
      this.contactGroups = this.contactGroups.filter((group) => group.contact_group_type !== 'Call');
      
      // Load contact count for email/whatsapp campaigns if contact group is selected
      if (this.campaignForm.get('contact_group_id')?.value) {
        this.onContactGroupChange(this.campaignForm.get('contact_group_id')?.value);
      }
    }


  }

  ngAfterViewInit() {
    if (this.active === 'whatsapp') {
      this.campaignForm.get('broadcast_type')?.setValue('whatsapp');
    } else if (this.active === 'email') {
      this.campaignForm.get('broadcast_type')?.setValue('email');
      // Add subject validator for email campaigns
      this.campaignForm.get('subject')?.setValidators([Validators.required]);
      this.campaignForm.get('subject')?.updateValueAndValidity();
    } else if (this.active === 'call') {
      this.campaignForm.get('broadcast_type')?.setValue('call');
      this.campaignForm.get('template_id')?.clearValidators();
      this.campaignForm.get('template_id')?.updateValueAndValidity();
    }
  }

  onScheduleChange(event: any) {
    if (event.checked) {
      this.campaignForm.get('schedule_date')?.setValidators([Validators.required, futureDateValidator]);
    } else {
      this.campaignForm.get('schedule_date')?.clearValidators();
      this.campaignForm.get('schedule_time')?.clearValidators();
    }

    this.campaignForm.get('schedule_date')?.updateValueAndValidity();
    this.campaignForm.get('schedule_time')?.updateValueAndValidity();
  }

  handleCloseDialog() {
    this.closeDialog.emit();
  }

  onSubmit() {
    this.isSubmitted = true;
    console.log(this.campaignForm.value);

    // Check if marketing_campaign credentials exist before submitting email campaign
    if (this.active === 'email') {
      const marketingCampaignCredentials = this.localStorage.getModuleMapping('marketing_campaign');
      
      if (!marketingCampaignCredentials) {
        this.handleCloseDialog();
        this.confirmationService.confirm({
          key: 'emailConfigSteps',
          header: 'Email Configuration Required',
          message: 'Please connect your domain and assign a dedicated email address to send marketing campaigns.',
          acceptLabel: 'Connect Now',
          rejectLabel: 'Cancel',
          rejectVisible: true,
          accept: () => {
            this.router.navigate(['/main/settings/configuration-panel']);
          },
          reject: () => {
            this.confirmationService.close();
          }
        });
        return;
      }
    }

    if (this.campaignForm.valid) {

      // total time needed 
      let totalTimeNeeded = 0;

      if (this.active === 'call') {
        if (this.selectedNumberOfBatch && this.selectedBatchDelay) {
          totalTimeNeeded = (this.selectedNumberOfBatch - 1) * (this.selectedBatchDelay * 60);

          console.log("totalTimeNeeded after batch delay", totalTimeNeeded);
        }

        if (this.selectedCallDelay) {
          totalTimeNeeded += (this.selectedCallDelay * this.maxBatchSize);
          console.log("totalTimeNeeded after call delay", totalTimeNeeded);
        }
      }

      console.log("totalTimeNeeded", totalTimeNeeded);

      // totalTimeNeeded can not be more than 15 minutes
      // if (totalTimeNeeded > (15 * 60)) {
      //   this.messageService.add({
      //     severity: 'error',
      //     summary: 'Error',
      //     detail: 'Total time needed for all calls should not be more than 15 minutes. Please reduce number of batches or batch delay or call delay.',
      //     life: 7000 // visible for 7 seconds (7000 ms)
      //   });
      //   return;
      // }



      let scheduleDate = this.campaignForm.get('schedule_date')?.value;
      let scheduleTime = this.campaignForm.get('schedule_time')?.value;

      if (this.active === 'call') {
        if (this.isSchedule) {

          if (!this.call_schedule_date_time) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please select schedule date and time' });
            return;
          }

          // Validate that scheduled date is not in the past
          const currentTime = new Date();
          if (this.call_schedule_date_time < currentTime) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Cannot schedule campaign for past date and time' });
            return;
          }

          scheduleDate = this.call_schedule_date_time;
          scheduleTime = this.call_schedule_date_time;

        }else{
          scheduleDate = null;
          scheduleTime = null;
        }
      }

      console.log("scheduleDate and Time ", scheduleDate);

      // Convert scheduleDate to UTC format 'YYYY-MM-DDTHH:mmZ' for backend cron
      let formattedScheduleDate: string | null = null;
      if (scheduleDate) {
        formattedScheduleDate = moment.utc(scheduleDate).format("YYYY-MM-DDTHH:mmZ");
      }

      // Determine status based on schedule_date presence
      let status = 'Pending'; // Default status
      
      // If schedule_date is provided (not empty, not null, not empty string), set status to Schedule
      if (formattedScheduleDate && formattedScheduleDate.trim() !== '') {
        status = 'Schedule';
      }

      const payload: any = {
        campaign_name: this.campaignForm.get('event_name')?.value,
        campaign_type: this.campaignForm.get('broadcast_type')?.value,
        template_id: this.campaignForm.get('template_id')?.value,
        organization_id: this.localStorage.getOrganizationId(),
        contact_group_id: this.campaignForm.get('contact_group_id')?.value,
        is_schedule: this.campaignForm.get('is_schedule')?.value,
        schedule_date: formattedScheduleDate,
        schedule_time: formattedScheduleDate,
        status: status,
        user_id: this.localStorage.getUserId(),
      };

      // Add subject for email campaigns in additional_details
      if (this.active === 'email') {
        payload.additional_details = {};
        payload.additional_details.subject = this.campaignForm.get('subject')?.value;
      }

      if (this.active === 'call') {
        payload.additional_details = {};
        payload.additional_details.number_of_batches = this.selectedNumberOfBatch;
        payload.additional_details.batch_delay = this.selectedBatchDelay;
        payload.additional_details.call_delay = this.selectedCallDelay;
      }

      if (this.active == 'call' && !this.isSchedule) {
        payload.is_schedule = false;
        payload.schedule_date = null;
        payload.schedule_time = null;
      }

      this.btnLoading = true;

      if (this.type === 'Create') {
        this.api.createCampaign(payload).subscribe(
          (res: any) => {
            this.btnLoading = false;
            this.handleCloseDialog();
            this.reload.emit();
          },
          (error: any) => {
            // console.error('Error creating campaign', error);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error creating campaign' });
            this.btnLoading = false;
          }
        );
      } else {
        const payload2 = {
          id: this.campaignData.id,
          ...payload
        }

        let scheduleDate = this.campaignForm.get('schedule_date')?.value;
        let scheduleTime = this.campaignForm.get('schedule_time')?.value;

        if (this.active === 'call') {
          if (this.isSchedule) {
            if (!this.call_schedule_date_time) {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please select schedule date and time' });
              return;
            }

            scheduleDate = this.call_schedule_date_time;
            scheduleTime = this.call_schedule_date_time;
          }else{
            scheduleDate = null;
            scheduleTime = null;
          }
        }

        // Convert scheduleDate to UTC format 'YYYY-MM-DDTHH:mmZ' for backend cron
        let formattedScheduleDateUpdate: string | null = null;
        if (scheduleDate) {
          formattedScheduleDateUpdate = moment.utc(scheduleDate).format("YYYY-MM-DDTHH:mmZ");
        }

        payload2.schedule_date = formattedScheduleDateUpdate;
        payload2.schedule_time = formattedScheduleDateUpdate;

        // Determine status for update
        let updateStatus = 'Pending'; // Default status
        if (formattedScheduleDateUpdate && formattedScheduleDateUpdate.trim() !== '') {
          updateStatus = 'Schedule';
        } else if (this.active === 'whatsapp') {
          updateStatus = this.campaignData.status || 'Pending';
        }
        payload2.status = updateStatus;

        if (this.active === 'call') {
          payload2.additional_details.number_of_batches = this.selectedNumberOfBatch;
          payload2.additional_details.batch_delay = this.selectedBatchDelay;
          payload2.additional_details.call_delay = this.selectedCallDelay;
        }



        this.api.updateCampaign(payload2).subscribe(
          (res: any) => {
            this.btnLoading = false;
            this.handleCloseDialog();
            console.log('Campaign updated successfully', res);
            this.reload.emit();
          },
          (error: any) => {
            console.error('Error updating campaign', error);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error updating campaign' });
            this.btnLoading = false;
          }
        );
      }



    } else {

      console.log(this.campaignForm);
      // errq
      console.log(this.campaignForm.get('event_name')?.errors);
      console.log(this.campaignForm.get('broadcast_type')?.errors);
      console.log(this.campaignForm.get('template_id')?.errors);
      console.log(this.campaignForm.get('contact_group_id')?.errors);
      console.log(this.campaignForm.get('status')?.errors);
      
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill all required fields correctly.' });

    }
  }


  onContactGroupChange(event: any) {
    console.log("event selected", event);

    if (event === null || event === undefined || event === '') {
      return;
    }

    this.loadingBatchSize = true;

    // For call campaigns, use getAllContactGroupListFromDynamoDB
    if (this.active === 'call') {
      const payload = {
        groupId: event,
        campusId: this.localStorage.getCampusId(),
        organizationId: this.localStorage.getOrganizationId()
      };

      this.contactGroupService.getAllContactGroupListFromDynamoDB(payload).subscribe({
        next: (res: any) => {
          this.maxBatchSize = res.total_contacts;
          this.loadingBatchSize = false;
          this.disabledBatchSizeInput = false;
        },
        error: (err: any) => {
          this.loadingBatchSize = false;
          this.disabledBatchSizeInput = true;
          this.maxBatchSize = null;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error getting contact group list' });
          console.error('Error getting contact group list', err);
        }
      });
    } 
    // For email and whatsapp campaigns, use getAllContactGroupList
    else if (this.active === 'email' || this.active === 'whatsapp') {
      const payload = {
        filter: null,
        page: 1,
        limit: 1,
        order: {
          col: '"contact_group_list"."updatedAt"',
          dir: 'DESC'
        },
        where: {
          organization_id: this.localStorage.getOrganizationId(),
          contact_group_id: event
        }
      };

      this.contactGroupService.getAllContactGroupList(payload).subscribe({
        next: (res: any) => {
          this.maxBatchSize = res.count || 0;
          this.loadingBatchSize = false;
        },
        error: (err: any) => {
          this.loadingBatchSize = false;
          this.maxBatchSize = null;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error getting contact group details' });
          console.error('Error getting contact group details', err);
        }
      });
    }

  }

  // Navigation methods for creating new items
  createWhatsappTemplate() {
    this.router.navigate(['/main/settings/configuration-whatsapp-templates']);
  }

  createEmailTemplate() {
    this.router.navigate(['/main/settings/configuration-email-templates']);
  }

  createContactGroup() {
    this.router.navigate(['/main/settings/configuration-contact-groups']);
  }

  createDialingGroup() {
    this.router.navigate(['/main/settings/configuration-dialing-groups']);
  }

}
